import * as React from "react";
import {
  Title,
  useDataProvider,
  useNotify,
  useRefresh,
  useGetList,
} from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Typography,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClub } from "../club/ClubContext";

type Account = {
  id: number;
  email: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
};


export function TeamAssignmentsPage() {
  const { clubId } = useClub();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const teamIdParam = searchParams.get("teamId");
  const selectedTeamId = teamIdParam ? Number(teamIdParam) : null;

  const [selectedAdd, setSelectedAdd] = React.useState<number[]>([]);
  const [selectedRemove, setSelectedRemove] = React.useState<number[]>([]);

  // ✅ Hooks immer aufrufen — aber nur aktivieren, wenn teamId + clubId vorhanden
  const canLoad = !!clubId && !!selectedTeamId;

  const initials = (u: Account) => {
    const name = u.display_name || `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
    if (!name) return u.email?.[0]?.toUpperCase() ?? "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const makeAbsolute = (url: string) => {
    if (!url) return undefined;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_CDN_URL ?? ""}/static/avatars/${url}`;
  };

  const unassignedQuery = useGetList<Account>(
    "users",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "email", order: "ASC" },
      filter: {
        clubId,
        teamId: selectedTeamId ?? undefined,
        unassigned: true,
      },
    },
    { enabled: canLoad }
  );

  const assignedQuery = useGetList<Account>(
    "users",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "email", order: "ASC" },
      filter: {
        clubId,
        teamId: selectedTeamId ?? undefined,
      },
    },
    { enabled: canLoad }
  );

  const assign = async () => {
    if (!selectedTeamId || !selectedAdd.length) return;

    const res = await dataProvider.create("users/teamAssignments", {
      data: {
        clubId,
        teamId: selectedTeamId,
        accountIds: selectedAdd,
        action: "assign",
      },
    });

    notify(
      `Hinzugefügt: ${res.data.assigned}, übersprungen: ${res.data.skipped}`,
      { type: "info" }
    );

    setSelectedAdd([]);
    refresh();
  };

  const unassign = async () => {
    if (!selectedTeamId || !selectedRemove.length) return;

    const res = await dataProvider.create("users/teamAssignments", {
      data: {
        clubId,
        teamId: selectedTeamId,
        accountIds: selectedRemove,
        action: "unassign",
      },
    });

    notify(`Entfernt: ${res.data.removed}`, { type: "info" });

    setSelectedRemove([]);
    refresh();
  };

  // ✅ Early return erst NACH den Hooks (jetzt ist ESLint ruhig)
  if (!selectedTeamId) {
    return (
      <Box p={2}>
        <Typography color="error">Kein Team ausgewählt.</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/teams")}
          sx={{ mt: 2 }}
        >
          Zurück zur Teamliste
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Title title="Team-Zuordnung" />

      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/teams")}>
          Zurück zur Teamliste
        </Button>
      </Box>

      {!clubId && (
        <Typography color="error">
          Kein Verein gewählt (clubId fehlt).
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Nicht im Team</Typography>

              {unassignedQuery.data?.map((u) => (
                <FormControlLabel
                  key={u.id}
                  control={
                    <Checkbox
                      checked={selectedAdd.includes(u.id)}
                      onChange={() =>
                        setSelectedAdd((prev) =>
                          prev.includes(u.id)
                            ? prev.filter((x) => x !== u.id)
                            : [...prev, u.id]
                        )
                      }
                    />
                  }
                  label={
  <Box display="flex" alignItems="center" gap={1}>
    <Avatar
      src={makeAbsolute(u.avatar_url ?? "")}
      sx={{
        width: 32,
        height: 32,
        fontSize: 14,
        fontWeight: 700,
        bgcolor: "primary.main",
      }}
    >
      {initials(u)}
    </Avatar>

    <Typography variant="body2">
      {u.display_name ?? u.email}
    </Typography>
  </Box>
}
                />
              ))}

              <Button
                sx={{ mt: 1 }}
                variant="contained"
                disabled={!selectedAdd.length || !canLoad}
                onClick={assign}
              >
                Hinzufügen
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Im Team</Typography>

              {assignedQuery.data?.map((u) => (
                <FormControlLabel
                  key={u.id}
                  control={
                    <Checkbox
                      checked={selectedRemove.includes(u.id)}
                      onChange={() =>
                        setSelectedRemove((prev) =>
                          prev.includes(u.id)
                            ? prev.filter((x) => x !== u.id)
                            : [...prev, u.id]
                        )
                      }
                    />
                  }
                  label={
  <Box display="flex" alignItems="center" gap={1}>
    <Avatar
      src={makeAbsolute(u.avatar_url ?? "")}
      sx={{
        width: 32,
        height: 32,
        fontSize: 14,
        fontWeight: 700,
        bgcolor: "primary.main",
      }}
    >
      {initials(u)}
    </Avatar>

    <Typography variant="body2">
      {u.display_name ?? u.email}
    </Typography>
  </Box>
}
                />
              ))}

              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                disabled={!selectedRemove.length || !canLoad}
                onClick={unassign}
              >
                Entfernen
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
